import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { departmentLabel } from "../lib/departmentLabel";
import { fmtMoneyShort } from "../lib/appUtils";
import { ink } from "../lib/ink";
import { TrendingUp, TrendingDown, Wallet, Landmark, Receipt, PiggyBank, Sparkles, RefreshCw, Loader2, CalendarDays, MessageSquare, AlertTriangle, Volume2, Square, Hourglass } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine } from "recharts";
import { toast } from "../components/ui/sonner";
import { fmtD } from "../lib/date";
import { periodLabel, prevLabel } from "./PeriodPicker";

const money = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();

/* ================= 顶部财务 KPI（含趋势） ================= */
function Spark({ data, dataKey, color }) {
  return (
    <ResponsiveContainer width="100%" height={34}>
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`sk-${dataKey}-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} fill={`url(#sk-${dataKey}-${color.slice(1)})`} dot={false} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function FinanceKpiRow({ period = "all" }) {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [d, setD] = useState(null);
  const isAll = !period || period === "all";
  useEffect(() => { api.get("/ceo/finance-overview", { params: { period: isAll ? "" : period } }).then(({ data }) => setD(data)).catch(() => {}); }, [api, period, isAll]);
  if (!d) return <div className="h-28 bg-white rounded-xl border border-slate-200 animate-pulse" data-testid="ceo-kpi-loading" />;
  const trend = (d.trend || []).map((r) => ({ ...r, arDelta: (r.contract || 0) - (r.collected || 0) }));
  const mom = (key) => {
    if (trend.length < 2) return null;
    const cur = trend[trend.length - 1][key] || 0;
    const prev = trend[trend.length - 2][key] || 0;
    if (!prev) return null;
    return Math.round(((cur - prev) / Math.abs(prev)) * 100);
  };
  const vsPrev = (key) => {
    const cur = (d.kpis || {})[key] || 0, prev = (d.prev || {})[key];
    if (prev == null || !prev) return null;
    return Math.round(((cur - prev) / Math.abs(prev)) * 100);
  };
  const k = d.kpis || {};
  const pl = periodLabel(period, zh);
  const cards = [
    { id: "contract", label: isAll ? (zh ? "合同总额" : "Contract") : (zh ? `${pl}签单额` : `Signed · ${pl}`), value: fmtMoneyShort(k.contract), icon: Receipt, color: "#0f172a", key: "contract", flow: true },
    { id: "collected", label: isAll ? (zh ? "已收款" : "Collected") : (zh ? `${pl}回款` : `Collected · ${pl}`), value: fmtMoneyShort(k.collected), icon: Wallet, color: "#16a34a", key: "collected", flow: true },
    { id: "ar", label: zh ? "应收账款 AR" : "Receivable AR", value: fmtMoneyShort(k.ar), icon: TrendingUp, color: "#2563eb", key: "arDelta", stock: true },
    { id: "ap", label: zh ? "应付账款 AP" : "Payable AP", value: fmtMoneyShort(k.ap), icon: TrendingDown, color: "#d97706", key: "ap", stock: true },
    { id: "profit", label: isAll ? (zh ? "毛利润" : "Gross Profit") : (zh ? `${pl}毛利` : `Gross profit · ${pl}`), value: fmtMoneyShort(k.profit), sub: `${zh ? "利润率" : "Margin"} ${k.margin}%`, icon: PiggyBank, color: "#7c3aed", key: "profit", flow: true },
    { id: "balance", label: zh ? "账户余额" : "Bank Balance", value: fmtMoneyShort(k.balance), icon: Landmark, color: "#0891b2", key: "balance", stock: true },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="ceo-kpi-row" data-period={d.period || "all"}>
      {cards.map((c) => {
        const chg = isAll ? mom(c.key) : (c.flow ? vsPrev(c.id) : null);
        const chgLabel = isAll ? (zh ? " 环比" : " MoM") : ` ${prevLabel(period, zh)}`;
        return (
          <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-3.5 relative overflow-hidden" data-testid={`ceo-kpi-${c.id}`}>
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-medium text-slate-500">{c.label}</p>
              <c.icon className="h-4 w-4" style={{ color: c.color }} />
            </div>
            <p className="text-xl font-extrabold text-slate-900 mt-1" title={money(c.id === "contract" ? k.contract : undefined)}>{c.value}</p>
            <div className="flex items-center gap-1.5 h-4 mt-0.5">
              {c.sub && <span className="text-[10px] font-semibold" style={ink(c.color)} data-testid="ceo-kpi-margin">{c.sub}</span>}
              {chg !== null && (
                <span className={`text-[10px] font-semibold inline-flex items-center gap-0.5 ${chg >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`ceo-kpi-mom-${c.id}`}>
                  {chg >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{chg >= 0 ? "+" : ""}{chg}%{chgLabel}
                </span>
              )}
              {!isAll && c.stock && <span className="text-[10px] text-slate-400" data-testid={`ceo-kpi-stock-${c.id}`}>{zh ? "截至今日存量" : "as of today"}</span>}
            </div>
            <div className="-mx-1 mt-1"><Spark data={trend} dataKey={c.key} color={c.color} /></div>
          </div>
        );
      })}
    </div>
  );
}

/* ================= Aligcus AI 今日简报 ================= */
const SEV = { high: { bg: "#fee2e2", c: "#b91c1c", zh: "紧急", en: "High" }, medium: { bg: "#fef3c7", c: "#92400e", zh: "关注", en: "Med" } };

export function AIDailyBrief({ period = "all" }) {
  const { api, lang } = useApp();
  const navigate = useNavigate();
  const zh = lang === "zh";
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [pd, setPd] = useState(null);
  const [cc, setCc] = useState(null);
  const audioRef = React.useRef(null);
  useEffect(() => { api.get("/finance/fixed-assets/counts/compliance").then(({ data }) => setCc(data)).catch(() => setCc(null)); }, [api]);
  const isAll = !period || period === "all";
  useEffect(() => {
    if (isAll) { setPd(null); return; }
    api.get("/ceo/finance-overview", { params: { period } }).then(({ data }) => setPd(data)).catch(() => setPd(null));
  }, [api, period, isAll]);
  const run = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.post("/ceo/daily-brief/run"); setBrief(data); }
    catch { /* keep old */ }
    setBusy(false);
  }, [api]);
  useEffect(() => {
    api.get("/ceo/daily-brief").then(({ data }) => {
      if (data && data.summary) setBrief(data);
      else run();
    }).catch(() => {});
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, [api, run]);
  const toggleAudio = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      return;
    }
    setLoadingAudio(true);
    try {
      const res = await api.get("/ceo/brief-audio", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "audio/mpeg" }));
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
      await audio.play();
      setPlaying(true);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? (zh ? "请先生成今日简报" : "Generate the brief first") : (zh ? "语音生成失败，请重试" : "Audio failed"));
    }
    setLoadingAudio(false);
  };
  const st = brief?.stats || {};
  const chips = brief ? [
    { icon: CalendarDays, label: zh ? `今日回访 ${st.schedule?.followupsToday ?? 0}` : `Follow-ups ${st.schedule?.followupsToday ?? 0}`, tid: "brief-chip-followups" },
    { icon: CalendarDays, label: zh ? `今日任务 ${st.schedule?.todosToday ?? 0}` : `Todos ${st.schedule?.todosToday ?? 0}`, tid: "brief-chip-todos" },
    { icon: MessageSquare, label: zh ? `24h 消息 ${st.messages24h ?? 0}` : `Msgs 24h ${st.messages24h ?? 0}`, tid: "brief-chip-msgs" },
    { icon: AlertTriangle, label: zh ? `逾期回访 ${st.departments?.sales_crm?.overdueFollowups ?? 0}` : `Overdue FU ${st.departments?.sales_crm?.overdueFollowups ?? 0}`, tid: "brief-chip-overdue" },
  ] : [];
  return (
    <div className="rounded-xl bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 p-5 text-white" data-testid="ceo-ai-brief">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-indigo-500/20 flex items-center justify-center"><Sparkles className="h-4 w-4 text-indigo-300" /></span>
          {zh ? "Aligcus AI 今日简报" : "Aligcus AI Daily Brief"}
          {brief?.date && <span className="text-xs font-normal text-slate-400">{fmtD(brief.date)}</span>}
        </h3>
        <div className="flex items-center gap-2">
          {brief?.summary && (
            <button data-testid="brief-audio-btn" onClick={toggleAudio} disabled={loadingAudio}
              className={`inline-flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 border transition-colors disabled:opacity-50 ${playing ? "text-red-300 border-red-400/50 bg-red-500/10" : "text-slate-300 border-slate-700 hover:border-emerald-400 hover:text-emerald-300"}`}>
              {loadingAudio ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : playing ? <Square className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
              {loadingAudio ? (zh ? "生成语音…" : "Loading…") : playing ? (zh ? "停止播报" : "Stop") : (zh ? "语音播报" : "Listen")}
            </button>
          )}
          <button data-testid="brief-refresh-btn" onClick={run} disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs text-slate-300 border border-slate-700 hover:border-indigo-400 hover:text-indigo-300 rounded-lg px-2.5 py-1.5 disabled:opacity-50 transition-colors">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}{busy ? (zh ? "生成中…" : "Generating…") : (zh ? "重新生成" : "Refresh")}
          </button>
        </div>
      </div>
      {!brief ? (
        <p className="text-sm text-slate-400 mt-4 flex items-center gap-2" data-testid="brief-loading"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "AI 正在读取日程、留言与各部门数据…" : "AI reading schedule, messages & department data…"}</p>
      ) : (
        <>
          {pd && pd.kpis && (() => {
            const pl = periodLabel(period, zh), k = pd.kpis, pv = pd.prev || {};
            const delta = (cur, prev) => (prev ? `${cur - prev >= 0 ? "+" : ""}${Math.round(((cur - prev) / Math.abs(prev)) * 100)}%` : "—");
            return (
              <p className="text-sm mt-3 bg-emerald-500/10 border border-emerald-400/30 rounded-lg px-3 py-2 text-emerald-100" data-testid="brief-period-summary" data-period={period}>
                📊 <b>{pl}</b>{pd.range ? <span className="text-emerald-300/80 text-xs">（{fmtD(pd.range.start)} ~ {fmtD(pd.range.end)}）</span> : null}：
                {zh ? "签单 " : "Signed "}<b>{fmtMoneyShort(k.contract)}</b><span className="text-xs text-emerald-300/80">（{prevLabel(period, zh)} {delta(k.contract, pv.contract)}）</span>，
                {zh ? "回款 " : "collected "}<b>{fmtMoneyShort(k.collected)}</b><span className="text-xs text-emerald-300/80">（{delta(k.collected, pv.collected)}）</span>，
                {zh ? "毛利 " : "gross profit "}<b>{fmtMoneyShort(k.profit)}</b><span className="text-xs text-emerald-300/80">（{zh ? "利润率" : "margin"} {k.margin}%）</span>
              </p>
            );
          })()}
          <p className="text-sm text-slate-200 mt-3 leading-relaxed" data-testid="brief-summary">{brief.summary}</p>
          {cc?.totals?.total > 0 && (
            <button type="button" onClick={() => navigate("/finance-center?tab=assets")} data-testid="brief-count-compliance" data-rate={cc.totals.rate}
              className={`w-full text-left text-sm mt-2 rounded-lg px-3 py-2 border transition-colors ${cc.totals.overdue ? "bg-red-500/10 border-red-400/30 text-red-100 hover:bg-red-500/20" : "bg-teal-500/10 border-teal-400/30 text-teal-100 hover:bg-teal-500/20"}`}>
              🗂 {zh ? `固定资产盘点合规率 ${cc.totals.rate}%` : `Asset count compliance ${cc.totals.rate}%`}
              <span className="text-xs opacity-80">{zh ? `（${cc.period?.label}：按时 ${cc.totals.counted} · 待盘 ${cc.totals.pending} · 逾期 ${cc.totals.overdue} · 从未盘点 ${cc.totals.never}${cc.totals.overdue ? ` — ${cc.branches.filter((b) => b.overdue).map((b) => `${b.label} ${b.overdue}`).join("、")}` : ""}）` : ` (${cc.period?.half}: on time ${cc.totals.counted} · pending ${cc.totals.pending} · overdue ${cc.totals.overdue} · never ${cc.totals.never})`}</span>
            </button>
          )}
          {brief.focus && (
            <p className="text-sm mt-2 bg-indigo-500/15 border border-indigo-400/30 rounded-lg px-3 py-2 text-indigo-200" data-testid="brief-focus">
              🎯 {zh ? "今日优先：" : "Focus: "}{brief.focus}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {chips.map((c) => (
              <span key={c.tid} data-testid={c.tid} className="inline-flex items-center gap-1 text-[11px] text-slate-300 bg-white/5 border border-white/10 rounded-full px-2.5 py-1">
                <c.icon className="h-3 w-3" />{c.label}
              </span>
            ))}
          </div>
          {(brief.urgent || []).length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="brief-urgent-list">
              {(brief.urgent || []).slice(0, 6).map((u, i) => {
                const s = SEV[u.severity] || SEV.medium;
                return (
                  <div key={i} className="flex flex-wrap items-start gap-2 text-sm" data-testid={`brief-urgent-${i}`}>
                    <span className="shrink-0 text-[10px] font-bold rounded px-1.5 py-0.5 mt-0.5" style={ink(s.c, s.bg)}>{zh ? s.zh : s.en}</span>
                    <span className="text-[11px] shrink-0 text-slate-400 mt-1">{departmentLabel(u.dept, { id: u.dept })}</span>
                    <span className="text-slate-200 text-[13px]">{u.text}</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ================= 现金流 12 月走势 + 3 月预测 + 安全水位 ================= */
export function CashflowChart({ period = "all" }) {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [rows, setRows] = useState(null);
  const [waterline, setWaterline] = useState(0);
  const [breaches, setBreaches] = useState([]);
  const [meta, setMeta] = useState({});
  const [editing, setEditing] = useState(false);
  const [wlInput, setWlInput] = useState("");
  const isAll = !period || period === "all";
  const load = useCallback(() => {
    api.get("/ceo/cashflow", { params: { months: 12, period: isAll ? "" : period } }).then(({ data }) => {
      setRows(data.months || []);
      setWaterline(Number(data.waterline || 0));
      setBreaches(data.breaches || []);
      setMeta({ gran: data.gran, range: data.range, totals: data.totals, period: data.period });
    }).catch(() => setRows([]));
  }, [api, period, isAll]);
  useEffect(() => { load(); }, [load]);
  const saveWl = async () => {
    try {
      const { data } = await api.put("/ceo/cashflow-waterline", { amount: Number(wlInput || 0) });
      setEditing(false);
      toast.success(zh ? `安全水位已设为 $${Number(data.amount).toLocaleString()}` : "Waterline saved");
      if ((data.breaches || []).length) toast.warning(zh ? `⚠️ 预测有 ${data.breaches.length} 个月净现金流低于水位，已提醒管理层` : `${data.breaches.length} forecast months below waterline`, { duration: 8000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  if (!rows) return <div className="h-72 bg-white rounded-xl border border-slate-200 animate-pulse" data-testid="cashflow-loading" />;
  const data = rows.map((r, i) => {
    const row = { month: r.label || r.month.slice(2) };
    if (r.forecast) {
      row.inflowF = r.inflow; row.outflowF = r.outflow; row.netF = r.net;
    } else {
      row.inflow = r.inflow; row.outflow = r.outflow; row.net = r.net;
      if (rows[i + 1]?.forecast) row.netF = r.net;
    }
    return row;
  });
  const pl = periodLabel(period, zh);
  const title = isAll ? (zh ? "现金流 12 月走势" : "12-Month Cash Flow")
    : (zh ? `${pl}现金流${meta.gran === "day" ? "（逐日）" : "（逐月）"}` : `Cash flow · ${pl}${meta.gran === "day" ? " (daily)" : " (monthly)"}`);
  const subtitle = isAll ? (zh ? "已收 vs 已付 vs 净现金流 · 含未来 3 个月预测（虚线）" : "In vs Out vs Net · 3-month forecast (dashed)")
    : (meta.totals ? (zh ? `期内已收 ${money(meta.totals.inflow)} · 已付 ${money(meta.totals.outflow)} · 净 ${money(meta.totals.net)}` : `In ${money(meta.totals.inflow)} · Out ${money(meta.totals.outflow)} · Net ${money(meta.totals.net)}`) : "");
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ceo-cashflow" data-period={meta.period || "all"}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold text-slate-900" data-testid="ceo-cashflow-title">{title}
          <span className="ml-2 text-xs font-normal text-slate-400" data-testid="ceo-cashflow-subtitle">{subtitle}</span>
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {breaches.length > 0 && (
            <span className="text-[11px] font-semibold bg-red-50 text-red-600 border border-red-200 rounded-full px-2.5 py-1" data-testid="waterline-breach-badge">
              ⚠ {zh ? `${breaches.length} 个月预测跌破水位` : `${breaches.length} months below waterline`}
            </span>
          )}
          {editing ? (
            <span className="inline-flex items-center gap-1.5">
              <input type="number" data-testid="waterline-input" value={wlInput} onChange={(e) => setWlInput(e.target.value)}
                placeholder={zh ? "安全水位 $" : "Waterline $"} className="w-32 text-xs rounded-md border border-slate-200 px-2 py-1.5" />
              <button data-testid="waterline-save-btn" onClick={saveWl} className="text-xs font-medium text-white bg-brand-500 hover:bg-brand-600 rounded-md px-2.5 py-1.5">{zh ? "保存" : "Save"}</button>
              <button onClick={() => setEditing(false)} className="text-xs text-slate-400 hover:text-slate-600">{zh ? "取消" : "Cancel"}</button>
            </span>
          ) : (
            <button data-testid="waterline-set-btn" onClick={() => { setWlInput(waterline || ""); setEditing(true); }}
              className="text-xs text-slate-500 border border-slate-200 hover:border-brand-300 hover:text-brand-500 rounded-md px-2.5 py-1.5 transition-colors">
              {waterline > 0 ? (zh ? `安全水位 $${Number(waterline).toLocaleString()}` : `Waterline $${Number(waterline).toLocaleString()}`) : (zh ? "设置安全水位" : "Set waterline")}
            </button>
          )}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} margin={{ top: 6, right: 8, left: -6, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
          <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
          <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#64748b" }} />
          <Tooltip formatter={(v, n) => [money(v), n]} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {waterline > 0 && <ReferenceLine y={waterline} stroke="#dc2626" strokeDasharray="4 4" strokeWidth={1.5}
            label={{ value: zh ? "安全水位" : "Waterline", fill: "#dc2626", fontSize: 11, position: "insideTopRight" }} />}
          <Bar dataKey="inflow" name={zh ? "已收" : "Inflow"} fill="#16a34a" radius={[4, 4, 0, 0]} barSize={14} />
          <Bar dataKey="outflow" name={zh ? "已付" : "Outflow"} fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={14} />
          <Bar dataKey="inflowF" name={zh ? "已收(预测)" : "Inflow (fcst)"} fill="#16a34a" fillOpacity={0.3} radius={[4, 4, 0, 0]} barSize={14} />
          <Bar dataKey="outflowF" name={zh ? "已付(预测)" : "Outflow (fcst)"} fill="#f59e0b" fillOpacity={0.3} radius={[4, 4, 0, 0]} barSize={14} />
          <Line type="monotone" dataKey="net" name={zh ? "净现金流" : "Net"} stroke="#4F6BFF" strokeWidth={2.5} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="netF" name={zh ? "净现金流(预测)" : "Net (fcst)"} stroke="#7B5CFF" strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ================= AR 账龄分析 ================= */
const BUCKET_COLORS = { current: "#64748b", d30: "#b45309", d60: "#c2410c", d90: "#dc2626", d90plus: "#b91c1c" };
const BUCKET_EN = { current: "Current", d30: "1-30 d", d60: "31-60 d", d90: "61-90 d", d90plus: "90+ d" };

export function ARAgingCard() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(null);
  const load = useCallback(() => { api.get("/ceo/ar-aging").then(({ data }) => setD(data)).catch(() => setD({ total: 0, buckets: [], items: [] })); }, [api]);
  useEffect(() => { load(); }, [load]);
  const remind = async (it) => {
    if (!window.confirm(zh ? `向 ${it.clientName || "客户"} 发送催收短信？\n发票 ${it.invoiceNumber} · ${"$" + Number(it.amount).toLocaleString()} · 逾期 ${it.days} 天` : `Send payment reminder SMS to ${it.clientName}?`)) return;
    setSending(it.id);
    try {
      const { data } = await api.post(`/ceo/ar-aging/${it.id}/remind`);
      toast.success(zh ? `催收短信已发送（第 ${data.remindCount} 次）` : `Reminder sent (#${data.remindCount})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSending(null);
  };
  if (!d) return <div className="h-48 bg-white rounded-xl border border-slate-200 animate-pulse" data-testid="ar-aging-loading" />;
  const total = d.total || 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ar-aging-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Hourglass className="h-4 w-4 text-red-600" />{zh ? "AR 账龄分析" : "AR Aging"}
          <span className="text-xs font-normal text-slate-400">{zh ? "未收发票按逾期天数分层" : "Outstanding invoices by days overdue"}</span>
        </h3>
        <span className="text-sm font-bold text-slate-800" data-testid="ar-aging-total">{zh ? "未收合计 " : "Total "}{money(total)}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        {(d.buckets || []).map((b) => (
          <div key={b.key} className="rounded-lg p-2.5 border" style={{ borderColor: BUCKET_COLORS[b.key] + "44", background: BUCKET_COLORS[b.key] + "0d" }} data-testid={`ar-bucket-${b.key}`}>
            <p className="text-[10px] font-semibold" style={ink(BUCKET_COLORS[b.key])}>{zh ? b.label : BUCKET_EN[b.key]}</p>
            <p className="text-base font-extrabold text-slate-800">{fmtMoneyShort(b.amount)}</p>
            <p className="text-[10px] text-slate-400">{b.count} {zh ? "张发票" : "invoices"}</p>
          </div>
        ))}
      </div>
      {total > 0 && (
        <div className="h-2.5 rounded-full overflow-hidden flex mb-4" data-testid="ar-aging-bar">
          {(d.buckets || []).filter((b) => b.amount > 0).map((b) => (
            <div key={b.key} style={{ width: `${(b.amount / total) * 100}%`, background: BUCKET_COLORS[b.key] }} title={`${b.label} ${money(b.amount)}`} />
          ))}
        </div>
      )}
      {(d.items || []).length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">{zh ? "暂无未收发票 ✓" : "No outstanding invoices ✓"}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-2">{zh ? "客户 / 地址" : "Client / Address"}</th>
              <th className="py-2 pr-2">{zh ? "发票号" : "Invoice"}</th>
              <th className="py-2 pr-2">{zh ? "销售" : "Sales"}</th>
              <th className="py-2 pr-2 text-right">{zh ? "金额" : "Amount"}</th>
              <th className="py-2 pr-2 text-right">{zh ? "到期日" : "Due"}</th>
              <th className="py-2 pr-2 text-right">{zh ? "逾期" : "Overdue"}</th>
              <th className="py-2 text-right">{zh ? "催收" : "Remind"}</th>
            </tr></thead>
            <tbody>
              {(d.items || []).map((it) => (
                <tr key={it.id} className="border-b border-slate-50" data-testid={`ar-item-${it.id}`}>
                  <td className="py-2 pr-2"><span className="font-medium text-slate-800">{it.clientName || "-"}</span><span className="block text-[11px] text-slate-400 truncate max-w-[220px]">{it.address}</span></td>
                  <td className="py-2 pr-2 text-slate-500 text-xs">{it.invoiceNumber}</td>
                  <td className="py-2 pr-2 text-slate-500 text-xs">{it.sales || "-"}</td>
                  <td className="py-2 pr-2 text-right font-semibold text-slate-800">{money(it.amount)}</td>
                  <td className="py-2 pr-2 text-right text-slate-500 text-xs">{fmtD(it.dueDate)}</td>
                  <td className="py-2 pr-2 text-right">
                    <span className="font-bold px-2 py-0.5 rounded-full text-[11px]" style={ink(BUCKET_COLORS[it.bucket], BUCKET_COLORS[it.bucket] + "1f")}>
                      {it.days > 0 ? `${it.days} ${zh ? "天" : "d"}` : (zh ? "未到期" : "current")}
                    </span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button data-testid={`ar-remind-${it.id}`} onClick={() => remind(it)} disabled={sending === it.id || it.days <= 0}
                      title={it.days <= 0 ? (zh ? "未到期无需催收" : "Not due yet") : ""}
                      className="text-[11px] font-semibold rounded-md px-2 py-1 border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-35 disabled:cursor-not-allowed transition-colors">
                      {sending === it.id ? (zh ? "发送中…" : "…") : (zh ? "一键催收" : "Remind")}
                    </button>
                    {it.remindCount > 0 && <span className="block text-[10px] text-slate-400 mt-0.5" data-testid={`ar-remind-info-${it.id}`}>{zh ? `已催 ${it.remindCount} 次` : `${it.remindCount}x`} {it.lastRemindAt ? `· ${it.lastRemindAt.slice(5)}` : ""}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================= 分公司待结算排行 ================= */
export function SettleBacklogCard() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/ceo/settle-backlog").then(({ data }) => setD(data)).catch(() => setD({ branches: [], total: 0, totalReceivable: 0 })); }, [api]);
  if (!d) return <div className="h-40 bg-white rounded-xl border border-slate-200 animate-pulse" data-testid="settle-backlog-loading" />;
  const daysCls = (dys) => (dys >= 30 ? "text-red-600 font-bold" : dys >= 15 ? "text-amber-600 font-semibold" : "text-slate-600");
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="settle-backlog-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Hourglass className="h-4 w-4 text-amber-600" />{zh ? "分公司待结算排行" : "Settlement Backlog by Branch"}
        </h3>
        <span className="text-xs text-slate-400" data-testid="settle-backlog-totals">
          {zh ? `共 ${d.total} 个已完工待结算 · 应收 ${money(d.totalReceivable)}` : `${d.total} pending · AR ${money(d.totalReceivable)}`}
        </span>
      </div>
      {d.branches.length === 0 ? (
        <p className="text-sm text-emerald-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5" data-testid="settle-backlog-empty">
          ✓ {zh ? "所有已完工项目均已结算，无积压" : "All completed projects settled"}
        </p>
      ) : (
        <div className="space-y-2">
          {d.branches.map((b, i) => (
            <div key={b.branch} data-testid={`settle-backlog-branch-${b.branch}`} className="border border-slate-100 rounded-lg overflow-hidden">
              <button onClick={() => setOpen(open === b.branch ? null : b.branch)} data-testid={`settle-backlog-toggle-${b.branch}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-slate-50 transition-colors text-left">
                <span className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 ${i === 0 ? "bg-red-100 text-red-700" : i === 1 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                <span className="font-semibold text-slate-800 w-24 shrink-0">{b.branchLabel}</span>
                <span className="text-xs text-slate-500">{zh ? "待结算" : "Pending"} <b className="text-slate-800">{b.count}</b> {zh ? "个" : ""}</span>
                <span className="text-xs text-slate-500 hidden sm:inline">{zh ? "应收" : "AR"} <b className={b.totalReceivable > 0 ? "text-red-600" : "text-emerald-600"}>{money(b.totalReceivable)}</b></span>
                <span className="text-xs ml-auto whitespace-nowrap">{zh ? "平均积压" : "Avg"} <b className={daysCls(b.avgDays)}>{b.avgDays}{zh ? "天" : "d"}</b> · {zh ? "最久" : "Max"} <b className={daysCls(b.maxDays)}>{b.maxDays}{zh ? "天" : "d"}</b></span>
              </button>
              {open === b.branch && (
                <div className="border-t border-slate-100 divide-y divide-slate-50" data-testid={`settle-backlog-projects-${b.branch}`}>
                  {b.projects.map((p) => (
                    <button key={p.id} data-testid={`settle-backlog-proj-${p.id}`} onClick={() => nav(`/finance/project/${p.id}`)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-xs hover:bg-amber-50/50 transition-colors text-left">
                      <span className="flex-1 min-w-0 truncate text-slate-700">{p.address} <span className="text-slate-400">· {p.clientName || "-"}</span></span>
                      <span className={`whitespace-nowrap ${daysCls(p.days || 0)}`}>{zh ? "积压" : ""} {p.days ?? "-"}{zh ? "天" : "d"}</span>
                      <span className="text-slate-500 whitespace-nowrap">{zh ? "毛利" : "GP"} <b className={p.profit >= 0 ? "text-slate-700" : "text-red-600"}>{money(p.profit)}</b></span>
                      <span className="text-indigo-600 font-medium whitespace-nowrap">{zh ? "去结算 →" : "Settle →"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
